import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import Logo from "./Logo";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "./NotificationBell";
import {
  LayoutDashboard, PackagePlus, FileDown, Search, Settings,
  Layers, Users, Radio, ListChecks, Activity, Palette, Receipt,
  Inbox, LogOut, Mail, ServerCog, ChevronRight, CreditCard, Code2,
  BarChart3, ShieldAlert, ShieldCheck, Signal, Target, MessageSquare,
} from "lucide-react";

const iconProps = { size: 16, strokeWidth: 1.5 };

function NavItem({ to, icon, label, testId }) {
  return (
    <NavLink
      to={to}
      end
      data-testid={testId}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors duration-150 ${
          isActive
            ? "bg-[color:var(--dt6-surface-2)] text-[color:var(--dt6-primary)] border-l-2 border-[color:var(--dt6-primary)] pl-[10px]"
            : "text-[color:var(--dt6-text)] hover:bg-[color:var(--dt6-surface-2)]"
        }`
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );
}

export default function PortalSidebar({ role }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const clientLinks = [
    { to: "/client", icon: <LayoutDashboard {...iconProps} />, label: "Dashboard", testId: "sb-client-dashboard" },
    { to: "/client/orders/new", icon: <PackagePlus {...iconProps} />, label: "New order", testId: "sb-client-new-order" },
    { to: "/client/universe", icon: <Target {...iconProps} />, label: "Universe counts", testId: "sb-client-universe" },
    { to: "/client/deliverables", icon: <FileDown {...iconProps} />, label: "Deliverables", testId: "sb-client-deliverables" },
    { to: "/client/pixel", icon: <Code2 {...iconProps} />, label: "Pixel", testId: "sb-client-pixel" },
    { to: "/client/audits", icon: <Search {...iconProps} />, label: "My audits", testId: "sb-client-audits" },
    { to: "/client/billing", icon: <CreditCard {...iconProps} />, label: "Billing", testId: "sb-client-billing" },
    { to: "/client/account", icon: <Settings {...iconProps} />, label: "Account", testId: "sb-client-account" },
  ];

  const partnerLinks = [
    { to: "/partner", icon: <LayoutDashboard {...iconProps} />, label: "Dashboard", testId: "sb-partner-dashboard" },
    { to: "/partner/orders/new", icon: <PackagePlus {...iconProps} />, label: "Submit order", testId: "sb-partner-new-order" },
    { to: "/partner/brand", icon: <Palette {...iconProps} />, label: "Brand settings", testId: "sb-partner-brand" },
    { to: "/partner/wholesale", icon: <Receipt {...iconProps} />, label: "Wholesale sheet", testId: "sb-partner-wholesale" },
  ];

  const buyerLinks = [
    { to: "/buyer", icon: <LayoutDashboard {...iconProps} />, label: "Dashboard", testId: "sb-buyer-dashboard" },
    { to: "/buyer/standing-orders", icon: <Signal {...iconProps} />, label: "Standing orders", testId: "sb-buyer-standing" },
    { to: "/buyer/leads", icon: <ListChecks {...iconProps} />, label: "My leads", testId: "sb-buyer-leads" },
    { to: "/buyer/ledger", icon: <Receipt {...iconProps} />, label: "Ledger", testId: "sb-buyer-ledger" },
    { to: "/buyer/profile", icon: <Settings {...iconProps} />, label: "Profile", testId: "sb-buyer-profile" },
  ];

  const adminLinks = [
    { to: "/admin", icon: <LayoutDashboard {...iconProps} />, label: "Metrics", testId: "sb-admin-metrics" },
    { to: "/admin/agent", icon: <MessageSquare {...iconProps} />, label: "Agent Studio", testId: "sb-admin-agent" },
    { to: "/admin/alignment", icon: <ShieldCheck {...iconProps} />, label: "Alignment (Standard)", testId: "sb-admin-alignment" },
    { to: "/admin/pipeline", icon: <Layers {...iconProps} />, label: "Pipeline", testId: "sb-admin-pipeline" },
    { to: "/admin/fulfillment", icon: <ListChecks {...iconProps} />, label: "Fulfillment", testId: "sb-admin-fulfillment" },
    { to: "/admin/integrations", icon: <ServerCog {...iconProps} />, label: "Integrations", testId: "sb-admin-integrations" },
    { to: "/admin/lead-exchange", icon: <Radio {...iconProps} />, label: "Lead exchange", testId: "sb-admin-lx" },
    { to: "/admin/adjudication", icon: <ShieldAlert {...iconProps} />, label: "Adjudication", testId: "sb-admin-adjudication" },
    { to: "/admin/reports", icon: <BarChart3 {...iconProps} />, label: "Reports", testId: "sb-admin-reports" },
    { to: "/admin/crm", icon: <Users {...iconProps} />, label: "CRM lite", testId: "sb-admin-crm" },
    { to: "/admin/config", icon: <ServerCog {...iconProps} />, label: "Config studio", testId: "sb-admin-config" },
    { to: "/admin/audit-log", icon: <Activity {...iconProps} />, label: "Audit log", testId: "sb-admin-log" },
    { to: "/admin/outbox", icon: <Mail {...iconProps} />, label: "Outbox", testId: "sb-admin-outbox" },
    { to: "/admin/adapter-queue", icon: <Inbox {...iconProps} />, label: "Adapter queue", testId: "sb-admin-adapters" },
  ];

  const links =
    role === "admin" ? adminLinks :
    role === "partner" ? partnerLinks :
    role === "buyer" ? buyerLinks :
    clientLinks;
  const roleLabel =
    role === "admin" ? "ADMIN" :
    role === "partner" ? "PARTNER" :
    role === "buyer" ? "BUYER" :
    "CLIENT";

  return (
    <aside
      className="w-60 border-r flex flex-col shrink-0"
      style={{ background: "var(--dt6-surface)", borderColor: "var(--dt6-border)" }}
      data-testid="portal-sidebar"
    >
      <div className="p-4 border-b" style={{ borderColor: "var(--dt6-border)" }}>
        <Logo size={22} />
        <div className="mt-2 font-mono text-[0.65rem] tracking-[0.16em] uppercase text-[color:var(--dt6-muted)]">
          / {roleLabel} PORTAL
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto" data-testid="portal-nav">
        {links.map((l) => (
          <NavItem key={l.to} {...l} />
        ))}
      </nav>
      <div className="p-3 border-t" style={{ borderColor: "var(--dt6-border)" }}>
        <div className="mb-2">
          <div className="text-xs text-[color:var(--dt6-muted)]">Signed in</div>
          <div className="text-sm truncate" data-testid="sb-user-email">{user?.email}</div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/" className="dt6-btn flex-1 text-xs justify-center" data-testid="sb-public-site">
            <ChevronRight size={12} /> Public site
          </Link>
          <button
            onClick={async () => { await logout(); nav("/"); }}
            className="dt6-btn text-xs"
            data-testid="sb-logout"
            title="Log out"
          >
            <LogOut size={14} />
          </button>
        </div>
      </div>
    </aside>
  );
}
